import React, { useState, useEffect } from 'react'
import { get } from 'lodash'
import { useHistory, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
  IconCheckmark,
  IconDisabled,
  List,
  StatusCard,
  TooltipHeader,
  SelectedFeaturesTags
} from 'shared/components'
import { defaultDateFormat, errorMsg } from 'shared/constants'
import {
  sendErrorReport,
  displayValue,
  formatDate,
  getLicenseStatus,
  makeProductDictionary
} from 'shared/helpers'
import { checkCompanyConfigField } from 'shared/companyConfig'
import { fetchLicenseUserLicenses } from 'src/LicenseUsers/actions'
import { transformSort } from 'src/customer/CustomersList/helpers'
import './styles.scss'

export default function LicensesTab() {
  const { userID, companyId } = useParams()
  const history = useHistory()
  const products = useSelector(state => get(state, 'products.list'))
  const [licenses, setLicenses] = useState([])
  const [licensesPage, setLicensesPage] = useState(0)
  const [licensesLoading, setLicensesLoading] = useState(true)
  const [licenseSort, setLicenseSort] = useState([
    {
      desc: true,
      id: 'created_at'
    }
  ])

  const updateLicenses = (page, sort) => {
    setLicensesLoading(true)
    fetchLicenseUserLicenses(companyId, userID, page, transformSort(sort))
      .then(res => {
        setLicenses(get(res, 'data'))
        setLicensesLoading(false)
      })
      .catch(err => {
        sendErrorReport(err, 'Cannot get customer licenses')
        setLicensesLoading(false)
      })
  }

  useEffect(() => {
    updateLicenses(licensesPage, licenseSort)
  }, [licensesPage, licenseSort])

  const handleLicenseSortChange = newSorted => {
    setLicenseSort(newSorted)
    updateLicenses(licensesPage, newSorted)
  }

  const handleLicensePageChange = page => {
    setLicensesPage(page)
    updateLicenses(page, licenseSort)
  }

  const redirectToLicensePage = rowData => {
    const orderId = get(rowData, 'original.order')
    const licenseId = get(rowData, 'original.id')
    history.push(`/${companyId}/orders/${orderId}/${licenseId}`)
  }

  const mappedProducts = makeProductDictionary(products)

  return (
    <List
      clickable
      columns={[
        {
          accessor: 'created_at',
          Header: __('Created on'),
          Cell: rowData => formatDate(rowData.value),
          width: 160
        },
        {
          accessor: 'license_key',
          Header: __('User'),
          Cell: rowData => {
            const isBundle = get(rowData, 'original.bundle_license')
            if (isBundle) return '-'
            const licenseProduct = products.find(p => p.id === get(rowData, 'original.product.id'))
            const isUserAuth = get(licenseProduct, 'authorization_method') === 'user'
            if (isUserAuth) {
              const licenseUsers = get(rowData, 'original.license_users') || []
              const usersNum = licenseUsers.length
              if (usersNum > 0) {
                const firstUser = get(licenseUsers, '[0].true_email')
                const isSingleUser = usersNum === 1
                if (isSingleUser) {
                  return `${firstUser}`
                }
                return `${firstUser} (+${usersNum - 1})`
              }
              return __(errorMsg.notAssigned)
            }

            const licenseKey = get(rowData, 'original.license_key')
            if (licenseKey) {
              return displayValue(licenseKey)
            }
            return '-'
          },
          width: 200,
          sortable: false
        },
        {
          accessor: 'product.id',
          Header: __('Product'),
          Cell: rowData => displayValue(mappedProducts[rowData.value]),
          maxWidth: 250
        },
        {
          accessor: 'product_features',
          Header: __('Product features'),
          headerClassName: 'text-center',
          Cell: rowData => {
            const isBundle = get(rowData, 'original.is_bundle')
            if (isBundle) return '-'
            return <SelectedFeaturesTags features={get(rowData, 'value') || []} />
          },
          sortable: false
        },
        {
          accessor: 'license_type',
          Header: __('License Type'),
          Cell: rowData => {
            const isBundle = get(rowData, 'original.is_bundle')
            if (isBundle) return '-'
            return displayValue(rowData.value)
          },
          maxWidth: 200
        },
        {
          accessor: 'validity_period',
          Header: () => TooltipHeader(__('Expiration Date')),
          Cell: rowData => {
            const isBundle = get(rowData, 'original.is_bundle')
            if (isBundle) return '-'
            return formatDate(rowData.value, defaultDateFormat)
          },
          maxWidth: 200
        },
        {
          accessor: 'times_activated',
          Header: __('Times activated'),
          Cell: rowData => {
            const isBundle = get(rowData, 'original.is_bundle')
            if (isBundle) return '-'
            return displayValue(rowData.value)
          },
          maxWidth: 150
        },
        {
          accessor: 'is_trial',
          className: 'text-center',
          Header: __('Is trial'),
          headerClassName: 'text-center',
          Cell: rowData => {
            const isBundle = get(rowData, 'original.is_bundle')
            if (isBundle) return '-'
            return rowData.value ? (
              <IconCheckmark color='#10ac84' height='14px' />
            ) : (
              <IconDisabled color='#aaa' height='14px' />
            )
          },
          width: 80,
          show: checkCompanyConfigField(companyId, 'isTrial')
        },
        {
          accessor: 'enabled',
          className: 'text-center',
          Header: __('Status'),
          headerClassName: 'text-center',
          Cell: rowData => {
            const isBundle = get(rowData, 'original.is_bundle')
            if (isBundle) return '-'
            const status = getLicenseStatus(
              get(rowData, 'original.enabled'),
              get(rowData, 'original.active')
            )
            return (
              <StatusCard
                status={status.positive ? 'success' : 'error'}
                text={__(status.message)}
              />
            )
          },
          minWidth: 100
        }
      ]}
      data={get(licenses, 'results', [])}
      loading={licensesLoading}
      manual
      page={licensesPage}
      pages={Math.ceil(get(licenses, 'count') / 20)}
      minRows={2}
      showPagination
      defaultSorted={licenseSort}
      onSortedChange={handleLicenseSortChange}
      onPageChange={handleLicensePageChange}
      handleClick={rowData => redirectToLicensePage(rowData)}
    />
  )
}
